"use client";

import React, { useRef, useMemo } from "react";
import { Editor } from "@tiptap/core";
import { TextSelection } from "prosemirror-state";
import { liftTarget } from "@tiptap/pm/transform";
import { Check, CheckSquare, ChevronRight, Code, Heading1, Heading2, Heading3, ListCollapse, ListOrdered, TextQuote, FileText, List, ListChecks, MessageSquare, Calculator, Columns, Layers } from "lucide-react";
import { DropdownMenuSearch, DropdownMenu, DropdownMenuDivider } from "@/components/tailwind/ui/dropdown-menu";
import type { DropdownMenuItemProps } from "@/components/tailwind/ui/dropdown-menu";
import { EditorBubbleMenuList } from "./EditorBubbleMenuList";
import { getDefaultColumnWidths } from "../column-layout-utils";

interface TurnIntoMenuProps {
  editor: Editor;
  node: any;
  position: number;
  onClose: () => void;
  onBack: () => void;
  anchorPosition: { top: number; left: number };
  showOnRight?: boolean;
}

interface TurnIntoOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  command: (editor: Editor) => void;
  isActive: (editor: Editor) => boolean;
  isVisible?: (editor: Editor, node: any) => boolean;
}

// True when the caret sits in the TITLE (first child) of a toggle — converting there means
// "convert the toggle itself", whereas a body child converts in place.
const isInToggleTitle = (editor: Editor) => {
  const $from = editor.state.selection.$from;
  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type.name === "toggle") return $from.index(d) === 0;
  }
  return false;
};

// If the caret is in a toggle's title, dissolve the toggle: lift ALL children out (title becomes
// a plain block, body children become its siblings, blockIds survive). Body children are left alone.
const unwrapToggleIfInTitle = (c: any) =>
  c.command(({ tr }: { tr: any }) => {
    const $from = tr.selection.$from;
    for (let d = $from.depth; d > 0; d--) {
      const node = $from.node(d);
      if (node.type.name === "toggle") {
        if ($from.index(d) !== 0) return true;
        const pos = $from.before(d);
        const $start = tr.doc.resolve(pos + 1);
        const $end = tr.doc.resolve(pos + node.nodeSize - 1);
        const range = $start.blockRange($end);
        const target = range && liftTarget(range);
        if (target != null) tr.lift(range, target);
        return true;
      }
    }
    return true;
  });

// Retype ONLY the title of the toggle containing the selection (never the body children —
// a full-content range from the drag handle would otherwise retype everything inside).
const retypeToggleTitle = (c: any, targetTypeName: string, attrs?: Record<string, any>) =>
  c.command(({ tr, state }: { tr: any; state: any }) => {
    const $from = tr.selection.$from;
    for (let d = $from.depth; d > 0; d--) {
      const node = $from.node(d);
      if (node.type.name === "toggle") {
        const titlePos = $from.start(d);
        const title = tr.doc.nodeAt(titlePos);
        const targetType = state.schema.nodes[targetTypeName];
        if (!title || !targetType || !title.isTextblock) return true;
        const merged = { ...title.attrs, ...attrs };
        if (title.hasMarkup(targetType, merged)) return true;
        if (!targetType.validContent(title.content)) tr.clearIncompatible(titlePos, targetType);
        tr.setNodeMarkup(titlePos, targetType, merged);
        return true;
      }
    }
    return true;
  });

// Lift the target block out of a quote/callout wrapper. Native `lift` moves the inner block
// node up unchanged — content, marks and blockId all survive.
const liftFromTextWrappers = (c: any, editor: Editor) => {
  c = unwrapToggleIfInTitle(c);
  if (editor.isActive("blockquote")) c = c.lift("blockquote");
  if (editor.isActive("callout")) c = c.lift("callout");
  return c;
};

// Leaf textblocks we may retype (paragraph / heading / codeBlock). Containers (callout, listItem,
// …) and atom blocks (image, page, …) are not textblocks, so this naturally excludes them.
const isLeafBlock = (node: any) => node.isTextblock;

// Schema-driven retype of every leaf block in the selection to `targetTypeName` (merging `attrs`),
// preserving each block's blockId. The ProseMirror schema makes every decision — NO heuristics
// (no block counting, no "touches a list", no "first block" index):
//   • Convert IN PLACE wherever the parent allows the new type at that position
//     (parent.canReplaceWith). So a heading stays inside a callout/column, and a list item's
//     NON-first block stays in the list — the schema permits both.
//   • A block whose parent forbids the type (only a list/task item's FIRST child, which must be a
//     paragraph) is LIFTED out of the list first, then converted.
//   • Same type already → skipped (never a toggle-off; a mixed selection still converts the rest).
//   • codeBlock holds only plain text, so incompatible inline content/marks are cleared first
//     (setNodeMarkup would otherwise throw "Invalid content").
// This can never produce a schema-invalid document and never nulls a blockId.
const retypeSelection = (c: any, targetTypeName: string, attrs?: Record<string, any>) =>
  c.command(({ tr, state }: { tr: any; state: any }) => {
    const targetType = state.schema.nodes[targetTypeName];
    if (!targetType) return true;
    const canPlace = (pos: number) => {
      const $pos = tr.doc.resolve(pos);
      return $pos.parent.canReplaceWith($pos.index(), $pos.index() + 1, targetType);
    };

    // PHASE A — lift out blocks the target type can't legally replace in place (a list/task item's
    // first child). Re-scan after each pass (lifts move positions); break when no forbidden blocks
    // remain OR no lift was actually applied this pass (blocks under an isolating boundary, e.g.
    // inside a column, can't be lifted — Phase B will skip them rather than crash). Termination
    // by "progress made" — not by count comparison — so deeper nesting where lifting one level
    // reveals another forbidden position still converges.
    for (let guard = 0; guard < 100; guard++) {
      const forbidden: number[] = [];
      tr.doc.nodesBetween(tr.selection.from, tr.selection.to, (node: any, pos: number) => {
        if (isLeafBlock(node) && !canPlace(pos)) forbidden.push(pos);
        return true;
      });
      if (forbidden.length === 0) break;
      let liftedAny = false;
      for (const pos of forbidden.sort((a, b) => b - a)) {
        const node = tr.doc.nodeAt(pos);
        if (!node) continue;
        const range = tr.doc.resolve(pos + 1).blockRange(tr.doc.resolve(pos + node.nodeSize - 1));
        const target = range && liftTarget(range);
        if (target != null) { tr.lift(range, target); liftedAny = true; }
      }
      if (!liftedAny) break; // every remaining forbidden block is at an isolating boundary
    }

    // PHASE B — retype every placeable target in place, preserving blockId. Process high→low so a
    // content-clearing step (codeBlock) can't shift an earlier (lower) position.
    const positions: number[] = [];
    tr.doc.nodesBetween(tr.selection.from, tr.selection.to, (node: any, pos: number) => {
      if (isLeafBlock(node)) positions.push(pos);
      return true;
    });
    for (const pos of positions.sort((a, b) => b - a)) {
      if (!canPlace(pos)) continue; // couldn't be lifted (isolating boundary) — skip, never break schema
      const node = tr.doc.nodeAt(pos);
      if (!node) continue;
      const merged = { ...node.attrs, ...attrs };
      if (node.hasMarkup(targetType, merged)) continue; // already this type — no-op, no toggle-off
      if (!targetType.validContent(node.content)) tr.clearIncompatible(pos, targetType);
      tr.setNodeMarkup(pos, targetType, merged);
    }
    return true;
  });

const LIST_ITEM_TYPES = new Set(["listItem", "taskItem"]);

// FLATTEN: lift every list/task item in the selection to top-level so its inner paragraph
// becomes loose. tr.lift on the item's CONTENT range preserves the inner paragraph node verbatim
// (blockId/marks/content survive). After this, no listItem/taskItem remains in the selection;
// what was inside them is now ordinary block content. Used before toggleBulletList/etc. so the
// native wrap operates on uniform paragraphs (preserving their blockIds) instead of rebuilding.
const flattenLists = (c: any) =>
  c.command(({ tr }: { tr: any }) => {
    for (let guard = 0; guard < 100; guard++) {
      let foundPos: number | null = null;
      let foundNode: any = null;
      tr.doc.nodesBetween(tr.selection.from, tr.selection.to, (node: any, pos: number) => {
        if (foundPos != null) return false;
        if (LIST_ITEM_TYPES.has(node.type.name)) { foundPos = pos; foundNode = node; return false; }
        return true;
      });
      if (foundPos == null) return true;
      const pos: number = foundPos;
      const $start = tr.doc.resolve(pos + 1);
      const $end = tr.doc.resolve(pos + foundNode.nodeSize - 1);
      const range = $start.blockRange($end);
      const target = range && liftTarget(range);
      if (target == null) return true; // isolating boundary — can't go further; leave as is
      tr.lift(range, target);
    }
    return true;
  });

// Wrap the selection into a list of `wrapperName`, preserving inner paragraph blockIds across
// mixed selections (heading + paragraph + bullet item + …). The pipeline:
//   (1) flattenLists — lift every list item so its inner paragraph becomes loose (lift preserves
//       blockId verbatim);
//   (2) retypeSelection(paragraph) — turn any heading/code into paragraph in place (setNodeMarkup
//       with merged attrs preserves blockId);
//   (3) the native TipTap toggle (toggleBulletList/etc.) — its wrap step only sees uniform
//       paragraphs, so it just wraps them via ReplaceAroundStep, keeping every blockId. (The id
//       loss in earlier versions came from toggleList's internal setBlockType paragraphize, which
//       drops blockId; doing the paragraphize ourselves in step 2 avoids that.)
const wrapAsList = (
  c: any,
  toggle: (chain: any) => any,
): any => toggle(retypeSelection(flattenLists(c), "paragraph"));

// Lift the target block out of its list/task item — but ONLY when it's the item's FIRST/only
// block. A list item must start with a paragraph, so its first child can't become a heading/code
// and therefore has to leave the list. A NON-first block, however, is a valid `block*` child and
// can be retyped IN PLACE (e.g. listItem > [paragraph, heading]) — lifting the whole item there
// would wrongly eject every block in the item. blockId is preserved either way.
const liftFromLists = (c: any, editor: Editor) => {
  const $from = editor.state.selection.$from;
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type.name === "listItem" || node.type.name === "taskItem") {
      if ($from.index(d) === 0) c = c.liftListItem(node.type.name);
      break;
    }
  }
  return c;
};

// Place a text selection inside the block at `position`, then run a native TipTap conversion
// on it. Using TipTap's own commands means the ORIGINAL content block is preserved:
//   • turning a paragraph into a list/quote/callout wraps that same paragraph — only the
//     wrapper nodes are new blocks (the paragraph keeps its identity);
//   • turning a wrapped block into another container lifts that same paragraph out first,
//     then re-wraps it — never nesting the new container inside the old one.
// blockIds are handled by the change tracker — we never reassign them by hand.
const runTurnInto = (
  editor: Editor,
  position: number,
  apply: (chain: any, editor: Editor) => any
) => {
  // STEP 1 — put the selection on the target block and commit it. Committing it first (instead
  // of inside the conversion chain) means the shared `apply` below sees this block via
  // editor.isActive() — so the drag-handle path runs the exact same rule as the floating
  // toolbar, which already operates on the user's live selection.
  try {
    const { doc } = editor.state;
    const node = doc.nodeAt(position);
    if (node) {
      const name = node.type.name;
      // Lists (the item OR the whole list container) must convert PER ITEM: place a collapsed
      // cursor in a single item. A RANGE over a whole list would convert every item and lift
      // them all out to the root. A cursor also maps correctly through liftListItem's list split
      // (a multi-position range maps to the wrong split).
      const perItem =
        name === "listItem" ||
        name === "taskItem" ||
        name === "bulletList" ||
        name === "orderedList" ||
        name === "taskList";
      if (perItem) {
        const inside = TextSelection.near(doc.resolve(Math.min(position + 1, doc.content.size)), 1);
        editor.chain().focus().setTextSelection(inside.from).run();
      } else {
        // Full content range — lets a container with several children (e.g. a quote holding 3
        // paragraphs) lift/wrap all of them together.
        const from = Math.min(position + 1, doc.content.size);
        const to = Math.max(from, Math.min(position + node.nodeSize - 1, doc.content.size));
        editor.chain().focus().setTextSelection({ from, to }).run();
      }
    }
  } catch {
    // Fall back to the current selection if the block position can't be resolved.
  }

  // STEP 2 — run the shared conversion against that selection. Identical call to the one the
  // floating toolbar makes, so both entry points follow the same rule.
  apply(editor.chain().focus(), editor).run();
};

// A turn-into command. Calling it (editor, node, position) targets the single block at
// `position` — the drag-handle menu path. Its `.chain` exposes the raw native transformation so
// a selection-driven caller (the floating toolbar) can run it across the CURRENT selection,
// which may span multiple blocks.
export type TurnIntoCommand = ((editor: Editor, node: any, position: number) => void) & {
  chain?: (chain: any, editor: Editor) => any;
};

// Build a turn-into command from a TipTap chain transformation. The transformation wraps/lifts/
// retypes the target with native commands so the original block is preserved; blockId assignment
// for any new wrapper/child blocks is left entirely to the editor's change tracker — the single
// owner of block create/update/type-change.
const createTurnIntoCommand = (apply: (chain: any, editor: Editor) => any): TurnIntoCommand => {
  const command: TurnIntoCommand = (editor, _node, position) => runTurnInto(editor, position, apply);
  command.chain = apply;
  return command;
};

// Resolve the block + its before-node position from the current selection, so the floating toolbar
// can reuse the same TURN_INTO_OPTIONS commands as the drag-handle menu.
export const resolveBlockFromSelection = (
  editor: Editor
): { node: any; position: number } | null => {
  const { selection } = editor.state;
  const sel = selection as any;

  // NodeSelection (drag handle): the node is directly selected.
  if (sel?.node && sel.node.type?.isBlock) {
    return { node: sel.node, position: selection.from };
  }

  // Text selection / cursor: walk up to the nearest content block, use its before-node position.
  const $from = selection.$from;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (
      node.type.isBlock &&
      node.type.name !== "doc" &&
      node.type.name !== "columnLayout" &&
      node.type.name !== "columnItem"
    ) {
      return { node, position: $from.before(depth) };
    }
  }
  return null;
};

export const TURN_INTO_OPTIONS: Array<Omit<TurnIntoOption, 'command'> & { command: (editor: Editor, node: any, position: number) => void }> = [
  {
    id: "text",
    label: "Text",
    icon: <FileText className="h-4 w-4" />,
    // Text: every list/task item in the selection leaves its list (works for
    // single AND multi-block selections via flattenLists, which lifts each item in the range).
    // Then retypeSelection turns headings/code into paragraphs in place. Callout/column content
    // stays nested (paragraph is valid there). All blockIds preserved.
    command: createTurnIntoCommand((c) => retypeSelection(flattenLists(unwrapToggleIfInTitle(c)), "paragraph")),
    isActive: (editor) =>
      editor.isActive("paragraph") &&
      !editor.isActive("bulletList") &&
      !editor.isActive("orderedList") &&
      !editor.isActive("taskList") &&
      !isInToggleTitle(editor),
  },
  {
    id: "heading1",
    label: "Heading 1",
    icon: <Heading1 className="h-4 w-4" />,
    command: createTurnIntoCommand((c) => retypeSelection(unwrapToggleIfInTitle(c), "heading", { level: 1 })),
    isActive: (editor) => editor.isActive("heading", { level: 1 }) && !isInToggleTitle(editor),
  },
  {
    id: "heading2",
    label: "Heading 2",
    icon: <Heading2 className="h-4 w-4" />,
    command: createTurnIntoCommand((c) => retypeSelection(unwrapToggleIfInTitle(c), "heading", { level: 2 })),
    isActive: (editor) => editor.isActive("heading", { level: 2 }) && !isInToggleTitle(editor),
  },
  {
    id: "heading3",
    label: "Heading 3",
    icon: <Heading3 className="h-4 w-4" />,
    command: createTurnIntoCommand((c) => retypeSelection(unwrapToggleIfInTitle(c), "heading", { level: 3 })),
    isActive: (editor) => editor.isActive("heading", { level: 3 }) && !isInToggleTitle(editor),
  },
  {
    id: "todoList",
    label: "To-do list",
    icon: <ListChecks className="h-4 w-4" />,
    command: createTurnIntoCommand((c, editor) => {
      if (editor.isActive("taskItem")) return c; // already a to-do — don't toggle it off
      return wrapAsList(liftFromTextWrappers(c, editor), (ch) => ch.toggleTaskList());
    }),
    isActive: (editor) => editor.isActive("taskItem"),
  },
  {
    id: "bulletList",
    label: "Bulleted list",
    icon: <List className="h-4 w-4" />,
    command: createTurnIntoCommand((c, editor) => {
      if (editor.isActive("bulletList")) return c; // already a bullet — don't toggle it off
      return wrapAsList(liftFromTextWrappers(c, editor), (ch) => ch.toggleBulletList());
    }),
    isActive: (editor) => editor.isActive("bulletList"),
  },
  {
    id: "numberedList",
    label: "Numbered list",
    icon: <ListOrdered className="h-4 w-4" />,
    command: createTurnIntoCommand((c, editor) => {
      if (editor.isActive("orderedList")) return c; // already numbered — don't toggle it off
      return wrapAsList(liftFromTextWrappers(c, editor), (ch) => ch.toggleOrderedList());
    }),
    isActive: (editor) => editor.isActive("orderedList"),
  },
  {
    id: "toggleList",
    label: "Toggle list",
    icon: <ChevronRight className="h-4 w-4" />,
    // In a toggle title: demote the title to a paragraph (toggle heading → toggle list).
    // Anywhere else: wrap the block(s) into a toggle whose title becomes a paragraph.
    command: createTurnIntoCommand((c, editor) => {
      if (isInToggleTitle(editor)) return retypeToggleTitle(c, "paragraph");
      c = liftFromLists(c, editor);
      if (editor.isActive("blockquote")) c = c.lift("blockquote");
      return retypeToggleTitle(c.wrapIn("toggle"), "paragraph");
    }),
    isActive: (editor) => isInToggleTitle(editor) && editor.isActive("paragraph"),
  },
  {
    id: "toggleHeading1",
    label: "Toggle heading 1",
    icon: <ListCollapse className="h-4 w-4" />,
    command: createTurnIntoCommand((c, editor) => {
      if (isInToggleTitle(editor)) return retypeToggleTitle(c, "heading", { level: 1 });
      c = liftFromLists(c, editor);
      if (editor.isActive("blockquote")) c = c.lift("blockquote");
      return retypeToggleTitle(c.wrapIn("toggle"), "heading", { level: 1 });
    }),
    isActive: (editor) => isInToggleTitle(editor) && editor.isActive("heading", { level: 1 }),
  },
  {
    id: "toggleHeading2",
    label: "Toggle heading 2",
    icon: <ListCollapse className="h-4 w-4" />,
    command: createTurnIntoCommand((c, editor) => {
      if (isInToggleTitle(editor)) return retypeToggleTitle(c, "heading", { level: 2 });
      c = liftFromLists(c, editor);
      if (editor.isActive("blockquote")) c = c.lift("blockquote");
      return retypeToggleTitle(c.wrapIn("toggle"), "heading", { level: 2 });
    }),
    isActive: (editor) => isInToggleTitle(editor) && editor.isActive("heading", { level: 2 }),
  },
  {
    id: "toggleHeading3",
    label: "Toggle heading 3",
    icon: <ListCollapse className="h-4 w-4" />,
    command: createTurnIntoCommand((c, editor) => {
      if (isInToggleTitle(editor)) return retypeToggleTitle(c, "heading", { level: 3 });
      c = liftFromLists(c, editor);
      if (editor.isActive("blockquote")) c = c.lift("blockquote");
      return retypeToggleTitle(c.wrapIn("toggle"), "heading", { level: 3 });
    }),
    isActive: (editor) => isInToggleTitle(editor) && editor.isActive("heading", { level: 3 }),
  },
  {
    id: "code",
    label: "Code",
    icon: <Code className="h-4 w-4" />,
    command: createTurnIntoCommand((c) => retypeSelection(unwrapToggleIfInTitle(c), "codeBlock")),
    isActive: (editor) => editor.isActive("codeBlock"),
  },
  {
    id: "quote",
    label: "Quote",
    icon: <TextQuote className="h-4 w-4" />,
    command: createTurnIntoCommand((c, editor) => {
      if (editor.isActive("blockquote")) return c; // already a quote — don't toggle it off
      c = unwrapToggleIfInTitle(c);
      c = liftFromLists(c, editor);
      if (editor.isActive("callout")) c = c.lift("callout");
      return c.toggleBlockquote();
    }),
    isActive: (editor) => editor.isActive("blockquote"),
  },
  {
    id: "callout",
    label: "Callout",
    icon: <MessageSquare className="h-4 w-4" />,
    command: createTurnIntoCommand((c, editor) => {
      if (editor.isActive("callout")) return c; // already a callout — don't nest another
      c = unwrapToggleIfInTitle(c);
      c = liftFromLists(c, editor);
      if (editor.isActive("blockquote")) c = c.lift("blockquote");
      return c.wrapIn("callout");
    }),
    isActive: (editor) => editor.isActive("callout"),
  },
  {
    id: "blockEquation",
    label: "Block equation",
    icon: <Calculator className="h-4 w-4" />,
    command: createTurnIntoCommand((c) => c.clearNodes().toggleCodeBlock()),
    isActive: (editor) => false,
    isVisible: () => false, // Hidden until supported
  },
  {
    id: "syncedBlock",
    label: "Synced block",
    icon: <Layers className="h-4 w-4" />,
    command: createTurnIntoCommand((c) => c.clearNodes()),
    isActive: (editor) => false,
    isVisible: () => false, // Hidden until supported
  },
  {
    id: "page",
    label: "Page",
    icon: <FileText className="h-4 w-4" />,
    command: createTurnIntoCommand((c) => c.clearNodes()),
    isActive: (editor) => false, // Page detection might need custom logic
    isVisible: () => false, // Should not convert blocks to pages
  },
  {
    id: "columns2",
    label: "2 columns",
    icon: <Columns className="h-4 w-4" />,
    command: (editor: Editor, node: any, position: number) => {
      // Convert to 2 columns layout - get current node content and wrap it
      const nodeSize = node.nodeSize || 1;
      const from = position;
      const to = position + nodeSize;
      const content = editor.state.doc.slice(from, to).content;
      const widthValues = getDefaultColumnWidths(2);
      editor.chain()
        .focus()
        .setTextSelection({ from, to })
        .deleteSelection()
        .insertContent({
          type: "columnLayout",
          attrs: { columns: 2, widths: widthValues.join(",") },
          content: widthValues.map((width, index) => ({
            type: "columnItem",
            attrs: { width },
            content: index === 0 && content.size > 0 ? content.toJSON() : [{ type: "paragraph" }],
          })),
        })
        .run();

      // Move cursor into the first column's first paragraph
      setTimeout(() => {
        const { state } = editor;
        const { doc } = state;
        let columnLayoutPos = -1;
        let minDistance = Infinity;
        doc.descendants((node, pos) => {
          if (node.type.name === "columnLayout") {
            const distance = Math.abs(pos - from);
            if (distance < minDistance) {
              minDistance = distance;
              columnLayoutPos = pos;
            }
          }
        });
        if (columnLayoutPos !== -1) {
          const layoutNode = doc.nodeAt(columnLayoutPos);
          if (layoutNode && layoutNode.firstChild) {
            const firstColumnItemPos = columnLayoutPos + 1;
            const firstColumnNode = layoutNode.firstChild;
            let paragraphPos = -1;
            firstColumnNode.descendants((node, pos) => {
              if (node.type.name === "paragraph" && paragraphPos === -1) {
                paragraphPos = firstColumnItemPos + pos + 1;
                return false;
              }
            });
            if (paragraphPos !== -1) {
              editor.chain().setTextSelection(paragraphPos).focus().run();
            }
          }
        }
      }, 10);
    },
    isActive: (editor) => false,
    isVisible: () => false, // Hide columns conversion for now
  },
  {
    id: "columns3",
    label: "3 columns",
    icon: <Columns className="h-4 w-4" />,
    command: (editor: Editor, node: any, position: number) => {
      // Convert to 3 columns layout
      const nodeSize = node.nodeSize || 1;
      const from = position;
      const to = position + nodeSize;
      const content = editor.state.doc.slice(from, to).content;
      const widthValues = getDefaultColumnWidths(3);
      editor.chain()
        .focus()
        .setTextSelection({ from, to })
        .deleteSelection()
        .insertContent({
          type: "columnLayout",
          attrs: { columns: 3, widths: widthValues.join(",") },
          content: widthValues.map((width, index) => ({
            type: "columnItem",
            attrs: { width },
            content: index === 0 && content.size > 0 ? content.toJSON() : [{ type: "paragraph" }],
          })),
        })
        .run();

      // Move cursor into the first column's first paragraph
      setTimeout(() => {
        const { state } = editor;
        const { doc } = state;
        let columnLayoutPos = -1;
        let minDistance = Infinity;
        doc.descendants((node, pos) => {
          if (node.type.name === "columnLayout") {
            const distance = Math.abs(pos - from);
            if (distance < minDistance) {
              minDistance = distance;
              columnLayoutPos = pos;
            }
          }
        });
        if (columnLayoutPos !== -1) {
          const layoutNode = doc.nodeAt(columnLayoutPos);
          if (layoutNode && layoutNode.firstChild) {
            const firstColumnItemPos = columnLayoutPos + 1;
            const firstColumnNode = layoutNode.firstChild;
            let paragraphPos = -1;
            firstColumnNode.descendants((node, pos) => {
              if (node.type.name === "paragraph" && paragraphPos === -1) {
                paragraphPos = firstColumnItemPos + pos + 1;
                return false;
              }
            });
            if (paragraphPos !== -1) {
              editor.chain().setTextSelection(paragraphPos).focus().run();
            }
          }
        }
      }, 10);
    },
    isActive: (editor) => false,
    isVisible: () => false, // Hide columns conversion for now
  },
  {
    id: "columns4",
    label: "4 columns",
    icon: <Columns className="h-4 w-4" />,
    command: (editor: Editor, node: any, position: number) => {
      // Convert to 4 columns layout
      const nodeSize = node.nodeSize || 1;
      const from = position;
      const to = position + nodeSize;
      const content = editor.state.doc.slice(from, to).content;
      const widthValues = getDefaultColumnWidths(4);
      editor.chain()
        .focus()
        .setTextSelection({ from, to })
        .deleteSelection()
        .insertContent({
          type: "columnLayout",
          attrs: { columns: 4, widths: widthValues.join(",") },
          content: widthValues.map((width, index) => ({
            type: "columnItem",
            attrs: { width },
            content: index === 0 && content.size > 0 ? content.toJSON() : [{ type: "paragraph" }],
          })),
        })
        .run();

      // Move cursor into the first column's first paragraph
      setTimeout(() => {
        const { state } = editor;
        const { doc } = state;
        let columnLayoutPos = -1;
        let minDistance = Infinity;
        doc.descendants((node, pos) => {
          if (node.type.name === "columnLayout") {
            const distance = Math.abs(pos - from);
            if (distance < minDistance) {
              minDistance = distance;
              columnLayoutPos = pos;
            }
          }
        });
        if (columnLayoutPos !== -1) {
          const layoutNode = doc.nodeAt(columnLayoutPos);
          if (layoutNode && layoutNode.firstChild) {
            const firstColumnItemPos = columnLayoutPos + 1;
            const firstColumnNode = layoutNode.firstChild;
            let paragraphPos = -1;
            firstColumnNode.descendants((node, pos) => {
              if (node.type.name === "paragraph" && paragraphPos === -1) {
                paragraphPos = firstColumnItemPos + pos + 1;
                return false;
              }
            });
            if (paragraphPos !== -1) {
              editor.chain().setTextSelection(paragraphPos).focus().run();
            }
          }
        }
      }, 10);
    },
    isActive: (editor) => false,
    isVisible: () => false, // Hide columns conversion for now
  },
  {
    id: "columns5",
    label: "5 columns",
    icon: <Columns className="h-4 w-4" />,
    command: (editor: Editor, node: any, position: number) => {
      // Convert to 5 columns layout
      const nodeSize = node.nodeSize || 1;
      const from = position;
      const to = position + nodeSize;
      const content = editor.state.doc.slice(from, to).content;
      const widthValues = getDefaultColumnWidths(5);
      editor.chain()
        .focus()
        .setTextSelection({ from, to })
        .deleteSelection()
        .insertContent({
          type: "columnLayout",
          attrs: { columns: 5, widths: widthValues.join(",") },
          content: widthValues.map((width, index) => ({
            type: "columnItem",
            attrs: { width },
            content: index === 0 && content.size > 0 ? content.toJSON() : [{ type: "paragraph" }],
          })),
        })
        .run();

      // Move cursor into the first column's first paragraph
      setTimeout(() => {
        const { state } = editor;
        const { doc } = state;
        let columnLayoutPos = -1;
        let minDistance = Infinity;
        doc.descendants((node, pos) => {
          if (node.type.name === "columnLayout") {
            const distance = Math.abs(pos - from);
            if (distance < minDistance) {
              minDistance = distance;
              columnLayoutPos = pos;
            }
          }
        });
        if (columnLayoutPos !== -1) {
          const layoutNode = doc.nodeAt(columnLayoutPos);
          if (layoutNode && layoutNode.firstChild) {
            const firstColumnItemPos = columnLayoutPos + 1;
            const firstColumnNode = layoutNode.firstChild;
            let paragraphPos = -1;
            firstColumnNode.descendants((node, pos) => {
              if (node.type.name === "paragraph" && paragraphPos === -1) {
                paragraphPos = firstColumnItemPos + pos + 1;
                return false;
              }
            });
            if (paragraphPos !== -1) {
              editor.chain().setTextSelection(paragraphPos).focus().run();
            }
          }
        }
      }, 10);
    },
    isActive: (editor) => false,  
    isVisible: () => false, // Hide columns conversion for now
  },
];

export const TurnIntoMenu: React.FC<TurnIntoMenuProps> = ({
  editor,
  node,
  position,
  onClose,
  onBack,
  anchorPosition,
  showOnRight = false,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Filter options based on search and visibility
  const filteredOptions = useMemo(() => {
    let options = TURN_INTO_OPTIONS;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      options = options.filter((option) =>
        option.label.toLowerCase().includes(query)
      );
    }
    return options.filter((option) => option.isVisible ? option.isVisible(editor, node) : true);
  }, [searchQuery, editor, node]);

  // Convert to menu items
  const menuItems: DropdownMenuItemProps[] = useMemo(() => {
    return filteredOptions.map((option) => {
      const isActive = option.isActive(editor);
      return {
        id: option.id,
        label: option.label,
        icon: option.icon,
        onClick: (e?: React.MouseEvent) => {
          // Prevent event from closing menu before command executes
          if (e) {
            e.preventDefault();
            e.stopPropagation();
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation();
            }
          }

          // Execute the command with node and position
          try {
            option.command(editor, node, position);
          } catch (error) {
            console.error("Error executing turn into command:", error);
          }

          // Close the menu after a small delay to ensure command executes
          setTimeout(() => {
            onClose();
          }, 50);
        },
        selected: isActive,
        rightElement: isActive ? <Check className="h-4 w-4" /> : undefined,
      };
    });
  }, [filteredOptions, editor, node, position, onClose]);

  // Use the provided left position (already calculated to be side by side with main menu)
  // Just ensure it doesn't go off screen
  const menuWidth = 220;
  const padding = 20;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = anchorPosition.left;

  // Ensure it doesn't go off screen
  if (left + menuWidth > viewportWidth - padding) {
    left = viewportWidth - menuWidth - padding;
  }
  if (left < padding) {
    left = padding;
  }

  // Calculate vertical position - align with main menu (side by side)
  // The submenu opens horizontally (to the side), aligned with the main menu's top
  const maxSubmenuHeight = viewportHeight * 0.5; // Reduced to 50vh max
  const estimatedSubmenuHeight = Math.min(300, maxSubmenuHeight); // Reduced cap at 300px

  // Align top of submenu with top of main menu
  let top = anchorPosition.top;

  // If submenu would cut off from bottom, adjust upward
  if (top + estimatedSubmenuHeight > viewportHeight - padding) {
    // Move up to fit within viewport
    top = viewportHeight - padding - estimatedSubmenuHeight;

    // If that pushes it above viewport, position at top with padding
    // (menu will scroll if needed, but won't cut off)
    if (top < padding) {
      top = padding;
    }
  }

  // Ensure it doesn't go above viewport
  if (top < padding) {
    top = padding;
  }

  // Cap the panel to the space actually below `top` — the max-h-[50vh] class
  // alone can't stop it hanging past the bottom when the estimate is short.
  const submenuMaxHeight = Math.max(120, Math.min(maxSubmenuHeight, viewportHeight - padding - top));

  // Close on outside click
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Don't close if clicking inside the menu
      if (menuRef.current && e.target && menuRef.current.contains(e.target as HTMLElement)) {
        return;
      }
      // Don't close if clicking on the main menu (parent)
      const mainMenu = document.querySelector('[role="dialog"][aria-modal="true"]');
      if (mainMenu && mainMenu.contains(e.target as HTMLElement)) {
        return;
      }
      onBack();
    };

    // Use a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [onBack]);

  // Keyboard navigation - Escape to go back
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onBack();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onBack]);

  return (
    <div
      ref={menuRef}
      className="fixed z-[10001] bg-modal border border-component rounded-lg shadow-lg w-[220px] max-w-[calc(100vw-24px)] max-h-[50vh] flex flex-col overflow-hidden  animate-in fade-in zoom-in-95 duration-100 ease-out"
      style={{
        top: `${top}px`,
        left: `${left}px`,
        maxHeight: `${submenuMaxHeight}px`,
      }}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
    >
      {/* Search Input */}
      <div className="flex-shrink-0 px-3 py-2">
        <DropdownMenuSearch
          placeholder="Search blocks…"
          value={searchQuery}
          onChange={setSearchQuery}
          variant="default"
          autoFocus={true}
        />
      </div>

      {/* Menu Items */}
      <div className="flex-1 overflow-y-auto min-h-0 clean-scrollbar">
        <div className="p-1">
          <EditorBubbleMenuList items={menuItems} editor={editor} />
        </div>
      </div>
    </div>
  );
};

